import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { listConnections, createConnection, testConnection, enqueueJob } from "@/lib/integrations.functions";
import { listCustomers } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { PROVIDER_FIELDS, PROVIDER_CATEGORY, providersFor } from "@/lib/integrations/categories";
import {
  getCustomerCartroverLink,
  upsertCartroverMerchant,
  deleteCartroverMerchant,
  enqueueCartroverMerchantSync,
} from "@/lib/cartrover/cartrover.functions";

export const Route = createFileRoute(
  "/_authenticated/app/admin/customers/$customerId/integrations",
)({ component: CustomerIntegrationsPage });

type Conn = {
  id: string; provider: string; label: string; env: string; status: string;
  customer_id: string | null;
  health_message: string | null; last_health_at: string | null;
};

const ORDER_SOURCE_PROVIDERS = providersFor("order_source");

function CustomerIntegrationsPage() {
  const { customerId } = Route.useParams();
  const fetchCustomers = useServerFn(listCustomers);
  const list = useServerFn(listConnections);
  const test = useServerFn(testConnection);
  const enqueue = useServerFn(enqueueJob);

  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: () => runServerFn(() => fetchCustomers(), "Loading customers"),
  });
  const customer = (customers ?? []).find((c: any) => c.id === customerId);
  const tenantId = customer?.tenant_id as string | undefined;

  const [wizardOpen, setWizardOpen] = useState(false);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "integration_connections", customerId],
    queryFn: () => list({ data: { customerId } }),
  });
  const conns = ((data?.connections ?? []) as Conn[]).filter(
    (c) => PROVIDER_CATEGORY[c.provider] !== "carrier",
  );

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" className="mb-1 -ml-2">
            <Link to="/app/admin/customers"><ArrowLeft className="h-3 w-3 mr-1" />Customers</Link>
          </Button>
          <h2 className="text-xl font-semibold">Integrations — {customer?.name ?? "…"}</h2>
          <p className="text-sm text-muted-foreground">Order sources and returns platforms. Carrier accounts live under Carriers.</p>
        </div>
        <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
          <DialogTrigger asChild><Button size="sm" disabled={!tenantId}>Add connection</Button></DialogTrigger>
          <Wizard
            tenantId={tenantId ?? ""}
            customerId={customerId}
            customerName={customer?.name ?? ""}
            onClose={() => { setWizardOpen(false); refetch(); }}
          />
        </Dialog>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : conns.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No connections yet.</CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {conns.map((c) => {
            const tenantWide = !c.customer_id;
            return (
              <Card key={c.id}>
                <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
                  <CardTitle className="text-base">
                    <Link to="/app/admin/integrations/$connectionId" params={{ connectionId: c.id }} className="hover:underline">
                      {c.label}
                    </Link>
                    <span className="ml-2 text-xs text-muted-foreground uppercase">{c.provider}</span>
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {tenantWide && <Badge variant="secondary">Tenant-wide</Badge>}
                    <Badge variant={c.status === "active" ? "default" : c.status === "error" ? "destructive" : "secondary"}>{c.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="text-muted-foreground">env: {c.env}{c.last_health_at ? ` · last check ${new Date(c.last_health_at).toLocaleString()}` : ""}</div>
                  {c.health_message && <div className="text-xs text-muted-foreground">{c.health_message}</div>}
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={async () => {
                      try { const r = await test({ data: { connectionId: c.id } }); toast[r.ok ? "success" : "error"](r.ok ? "Connection OK" : r.message ?? "Failed"); refetch(); }
                      catch (e) { toast.error((e as Error).message); }
                    }}>Test</Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      try { await enqueue({ data: { connectionId: c.id, kind: "sync_orders", payload: {} } }); toast.success("Sync queued"); }
                      catch (e) { toast.error((e as Error).message); }
                    }}>Sync orders</Button>
                    <Button asChild size="sm" variant="ghost">
                      <Link to="/app/admin/integrations/$connectionId" params={{ connectionId: c.id }}>Manage</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CartroverMerchantCard customerId={customerId} />
    </div>
  );
}

function CartroverMerchantCard({ customerId }: { customerId: string }) {
  const fetchLink = useServerFn(getCustomerCartroverLink);
  const upsert = useServerFn(upsertCartroverMerchant);
  const remove = useServerFn(deleteCartroverMerchant);
  const sync = useServerFn(enqueueCartroverMerchantSync);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["cartrover", "customer-link", customerId],
    queryFn: () => fetchLink({ data: { customerId } }),
  });

  const merchant = data?.merchant ?? null;
  const connections = data?.connections ?? [];
  const [connectionId, setConnectionId] = useState<string>("");
  const [crMerchantId, setCrMerchantId] = useState("");
  const [merchantName, setMerchantName] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    const cid = connectionId || merchant?.connection_id;
    if (!cid) return toast.error("Pick a CartRover connection");
    if (!crMerchantId.trim()) return toast.error("CartRover merchant id required");
    if (!merchantName.trim()) return toast.error("Merchant name required");
    setBusy(true);
    try {
      await upsert({
        data: {
          id: merchant?.id,
          connectionId: cid,
          customerId,
          cartroverMerchantId: crMerchantId.trim(),
          merchantName: merchantName.trim(),
          credentialMode: (merchant?.credential_mode ?? "parent") as "parent" | "merchant",
          orderImportEnabled: merchant?.order_import_enabled ?? true,
          inventorySyncEnabled: merchant?.inventory_sync_enabled ?? true,
          shipmentSyncEnabled: merchant?.shipment_sync_enabled ?? true,
          inventoryCalcMode: "on_hand_minus_allocated",
          active: true,
        },
      });
      toast.success("Merchant linked");
      setCrMerchantId(""); setMerchantName("");
      refetch();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">CartRover Merchant</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {isLoading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : merchant ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{merchant.merchant_name}</div>
                <div className="text-xs text-muted-foreground">
                  CartRover id: <code>{merchant.cartrover_merchant_id}</code> · {merchant.credential_mode} creds
                </div>
              </div>
              <div className="flex gap-2">
                <Button asChild size="sm" variant="outline">
                  <Link to="/app/admin/integrations/cartrover/$connectionId"
                        params={{ connectionId: merchant.connection_id }}>Manage</Link>
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  try { await sync({ data: { merchantId: merchant.id, kind: "cartrover_sync_orders_merchant" } }); toast.success("Order sync queued"); }
                  catch (e) { toast.error((e as Error).message); }
                }}>Sync orders</Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  try { await sync({ data: { merchantId: merchant.id, kind: "cartrover_sync_inventory_merchant" } }); toast.success("Inventory push queued"); }
                  catch (e) { toast.error((e as Error).message); }
                }}>Push inventory</Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
                  if (!confirm("Unlink merchant from this customer?")) return;
                  try { await remove({ data: { id: merchant.id } }); toast.success("Unlinked"); refetch(); }
                  catch (e) { toast.error((e as Error).message); }
                }}>Unlink</Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Orders: {merchant.order_import_enabled ? "on" : "off"} · Inventory: {merchant.inventory_sync_enabled ? "on" : "off"} · Shipments: {merchant.shipment_sync_enabled ? "on" : "off"}
            </div>
          </div>
        ) : connections.length === 0 ? (
          <p className="text-muted-foreground">No CartRover connection configured yet. Add one above.</p>
        ) : (
          <div className="space-y-2">
            <p className="text-muted-foreground">Link this customer to a CartRover merchant to enable order import, inventory sync, and tracking push.</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Connection</Label>
                <Select value={connectionId} onValueChange={setConnectionId}>
                  <SelectTrigger><SelectValue placeholder="Pick connection" /></SelectTrigger>
                  <SelectContent>
                    {connections.map((c: { id: string; label: string; env: string }) => (
                      <SelectItem key={c.id} value={c.id}>{c.label} ({c.env})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">CartRover merchant id</Label>
                <Input value={crMerchantId} onChange={(e) => setCrMerchantId(e.target.value)} placeholder="e.g. acme-shop" />
              </div>
              <div>
                <Label className="text-xs">Display name</Label>
                <Input value={merchantName} onChange={(e) => setMerchantName(e.target.value)} placeholder="Acme Shop" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={save} disabled={busy}>{busy ? "Linking…" : "Link merchant"}</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Wizard({ tenantId, customerId, customerName, onClose }:
  { tenantId: string; customerId: string; customerName: string; onClose: () => void }) {
  const create = useServerFn(createConnection);
  const [provider, setProvider] = useState<keyof typeof PROVIDER_FIELDS>(
    (ORDER_SOURCE_PROVIDERS[0] ?? "cartrover") as keyof typeof PROVIDER_FIELDS,
  );
  const [label, setLabel] = useState("");
  const [env, setEnv] = useState<"sandbox" | "production">("sandbox");
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const fields = PROVIDER_FIELDS[provider];

  async function submit() {
    if (!tenantId) return toast.error("No tenant");
    if (!label.trim()) return toast.error("Label required");
    for (const f of fields.creds) if (!creds[f.key]?.trim()) return toast.error(`${f.label} required`);
    setBusy(true);
    try {
      await create({ data: { tenantId, customerId, provider, label: label.trim(), env, credentials: creds } });
      toast.success("Connection created");
      onClose();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader><DialogTitle>Add integration for {customerName}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Provider</Label>
            <Select value={provider} onValueChange={(v) => { setProvider(v as keyof typeof PROVIDER_FIELDS); setCreds({}); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORDER_SOURCE_PROVIDERS.map((p) => (
                  <SelectItem key={p} value={p}>{PROVIDER_FIELDS[p].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Environment</Label>
            <Select value={env} onValueChange={(v) => setEnv(v as "sandbox" | "production")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">Sandbox</SelectItem>
                <SelectItem value="production">Production</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Label</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`${customerName} ${provider}`} />
        </div>
        <div className="space-y-2 pt-2 border-t">
          {fields.creds.map((f) => (
            <div key={f.key}>
              <Label>{f.label}</Label>
              <Input type={f.type ?? "text"} value={creds[f.key] ?? ""} onChange={(e) => setCreds((s) => ({ ...s, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}